/**
 * Zoom and pan math for the drawing board.
 *
 * The board is a fixed-size box (board pixels). A transform maps a board point
 * p to a screen point s = translate + scale * p, relative to the board's own
 * top-left corner. Pins are NOT inside the transformed layer: they are drawn at
 * boardToScreen(...) so they keep their 44 px tap target at every zoom level.
 */

export interface Transform {
  scale: number;
  x: number;
  y: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface BoxSize {
  width: number;
  height: number;
}

export const MIN_SCALE = 1;
export const MAX_SCALE = 4;

export const IDENTITY: Transform = { scale: 1, x: 0, y: 0 };

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Keeps the scale within [MIN_SCALE, MAX_SCALE] and the content covering the
 * board: you can never pan the drawing out of view.
 */
export function clampTransform(t: Transform, board: BoxSize): Transform {
  const scale = clamp(t.scale, MIN_SCALE, MAX_SCALE);
  const minX = board.width - board.width * scale;
  const minY = board.height - board.height * scale;
  return { scale, x: clamp(t.x, minX, 0), y: clamp(t.y, minY, 0) };
}

export function screenToBoard(t: Transform, s: Point): Point {
  return { x: (s.x - t.x) / t.scale, y: (s.y - t.y) / t.scale };
}

export function boardToScreen(t: Transform, p: Point): Point {
  return { x: t.x + p.x * t.scale, y: t.y + p.y * t.scale };
}

/** Zooms by `factor` keeping the board point under `focal` (screen) fixed. */
export function zoomAt(t: Transform, factor: number, focal: Point, board: BoxSize): Transform {
  const scale = clamp(t.scale * factor, MIN_SCALE, MAX_SCALE);
  const anchor = screenToBoard(t, focal);
  return clampTransform({ scale, x: focal.x - anchor.x * scale, y: focal.y - anchor.y * scale }, board);
}

export function panBy(t: Transform, dx: number, dy: number, board: BoxSize): Transform {
  return clampTransform({ scale: t.scale, x: t.x + dx, y: t.y + dy }, board);
}

function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Two-finger gesture: the board point that was under the starting midpoint
 * stays under the current midpoint, and the scale follows the finger spread.
 * Computed from the gesture START, not incrementally, so rounding does not drift.
 */
export function pinch(
  start: Transform,
  from: readonly [Point, Point],
  to: readonly [Point, Point],
  board: BoxSize,
): Transform {
  const d0 = distance(from[0], from[1]);
  const d1 = distance(to[0], to[1]);
  const factor = d0 > 0 ? d1 / d0 : 1;
  const scale = clamp(start.scale * factor, MIN_SCALE, MAX_SCALE);
  const anchor = screenToBoard(start, midpoint(from[0], from[1]));
  const mid = midpoint(to[0], to[1]);
  return clampTransform({ scale, x: mid.x - anchor.x * scale, y: mid.y - anchor.y * scale }, board);
}

/** Wheel delta (pixels) to a zoom factor; trackpads send many small deltas, mice few large ones. */
export function wheelFactor(deltaY: number): number {
  return Math.exp(-clamp(deltaY, -200, 200) * 0.002);
}

/** A pointer that moved less than this between down and up counts as a tap. */
export const TAP_SLOP_PX = 8;

export function isTap(down: Point, up: Point, slop = TAP_SLOP_PX): boolean {
  return distance(down, up) <= slop;
}
